import { Alert, Box, Button, Slide, Snackbar, styled, Tooltip } from '@mui/material';
import { SlideProps } from '@mui/material/Slide';
import { PractitionerPageStyles } from './PractitionerPage.styles';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { LocalDataTrack } from 'twilio-video';
import { useTranslation } from 'react-i18next';
import ControlPanel from '../components/ControlPanel/ControlPanel';
import ControlsPractitioner from '../components/ControlsPractitioner/ControlsPractitioner';
import LocalVideoPreview from '../components/LocalVideoPreview/LocalVideoPreview';
import PractitionerControlsProvider from '../components/PractionerControlsProvider';
import {
  PractitionerMediaPreviewHost,
  PractitionerMediaPreviewPortal,
} from '../components/PractitionerMediaPreviewPlayer/PractitionerMediaPreviewReversePortal';
import {
  PractitionerCameraHost,
  PractitionerCameraPortal,
} from '../components/PractitionerCameraPreview/PractitionerCameraReversePortal';
import { PractitionerGameHost } from '../components/PractitionerGamePreview/PractitionerGameReversePortal';
import { Theme } from '../components/PhaserApp/enums/Themes';
import VideoLoader from '../components/VideoLoader/VideoLoader';
import useAppContext from '../hooks/useAppContext/useAppContext';
import useAuthorizedPractitioner from '../hooks/useAuthorizedPractitioner/useAuthorizedPractitioner';
import useCurrentSessionState from '../hooks/useCurrentSessionState/useCurrentSessionState';
import useRoomState from '../hooks/useRoomState/useRoomState';
import useVideoContext from '../hooks/useVideoContext/useVideoContext';
import useVideoViewToggle from '../hooks/useVideoViewToggle/useVideoViewToggle';
import './PractitionerPage.scss';
import BackendService from '../services/BackendService';
import MediaProvider from '../components/MediaProvider';
import UploadProvider from '../components/MediaUploadProvider';
import SessionCheckDialog from '../components/SessionCheckDialog/SessionCheckDialog';
import PermissionReminder from '../helpers/PermissionReminder';
import permissionImage from '../assets/device-permission-reminder.png';
import waitingForClientIMG from '../assets/fail_to_start_call.png';
import { requestMedia } from '../utils/requestMedia';
import { metrics } from '../metrics/sentryMetrics';
import { getMediaPermissionStatus } from '../utils/mediaPermissions';
import { ensureLocalMediaTracks } from '../utils/ensureLocalMediaTracks';
import {
  ALERT_TRANSITION_MS,
  CALL_CONNECT_TIMEOUT_MS,
  CALL_START_TOKEN_TIMEOUT_MS,
  NETWORK_RECONNECT_BANNER_LINGER_MS,
  NETWORK_RECONNECT_TIMEOUT_MS,
} from '../utils/consts';
import AcceptanceBadge from '../components/AcceptanceBadge/AcceptanceBadge';
import { practitionerWrapper } from '../components/AcceptanceBadge/style';
import { getResponsiveSize } from '../utils';
import { getSessionCode } from '../utils/sessionUUIDToCode';
import usePractitionerControlsContext from '../hooks/usePractitionerControlsContext/usePractitionerControlsContext';

const Container = styled('div')({
  display: 'flex',
  gridTemplateRows: 'auto 1fr',
});

const Main = styled('main')({
  overflow: 'hidden',
  width: '75%',
  height: '100%',
  position: 'relative',

  '& .practitioner-preview': {
    height: '100%',
    top: 0,
    bottom: 0,
    pointerEvents: 'none',
  },
  '& .practitioner-preview .player-container, & .practitioner-preview .player-container-image': {
    position: 'absolute',
    left: '50%',
    bottom: `max(${getResponsiveSize(390)}, calc(1% + 300px))`,
    transform: 'translateX(-50%)',
    top: 'auto',
    right: 'auto',
    width: getResponsiveSize(640),
    maxWidth: '60%',
    minHeight: 'unset',
    padding: 0,
    justifyContent: 'center',
  },
  '& .practitioner-preview .player-container .player-overlay': {
    paddingTop: '56.25%',
    position: 'relative',
    top: 0,
    left: 0,
    right: 0,
    bottom: 'unset',
    width: '100%',
    '& .react-player': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      minHeight: 'unset',
    },
  },
  '& .practitioner-preview .player-container-image .player-overlay-image': {
    position: 'relative',
    top: 0,
    left: 0,
    width: '100%',
    minHeight: 'unset',
  },
});

interface MainSlotsProps {
  cameraPortalNode: HTMLDivElement | null;
  mediaPreviewPortalNode: HTMLDivElement | null;
  gamePortalNode: HTMLDivElement | null;
  isCallActive: boolean;
}

const MainAreaSlots = ({ cameraPortalNode, mediaPreviewPortalNode, gamePortalNode, isCallActive }: MainSlotsProps) => {
  const { viewSwapped, isMediaLoaded, hasBeenSaved, game, theme } = usePractitionerControlsContext();
  const hasMedia = Boolean(isMediaLoaded && hasBeenSaved);

  const mainHasCamera = isCallActive && !viewSwapped;
  // media in main when (no call AND not swapped) OR (call AND swapped)
  const mainHasMedia = hasMedia && isCallActive === viewSwapped;
  // game moves to main only when swapped AND exactly one of call/media is active
  const gameInMain = viewSwapped && isCallActive !== hasMedia;
  // Carry the child-theme landscape into the main area together with the task.
  const showChildBackground = gameInMain && theme === Theme.Child;
  // Promote the game (drop it down + enlarge) whenever it lives in the main area —
  // both pre-call and during a call. During a call the practitioner self-view sits
  // bottom-left; the promoted game's bottom floor keeps the game above it, so neither
  // covers the other. The call button is only lowered pre-call (CallButtonOverlaySlot).
  const gamePromoted = gameInMain;
  const gameSlotClass = gamePromoted ? 'game-promoted' : undefined;

  useEffect(() => {
    if (!game) return;
    // Phaser's built-in window-resize listener already does the right thing —
    // reads the current parent bounds, resizes the canvas, and fires the 'resize'
    // event that TaskScene.handleScaleResize uses to rebuild all task objects.
    // After re-parenting the canvas (main ↔ footer) we dispatch a synthetic resize
    // so that exact same path runs without the user actually resizing the window.
    // useEffect fires after paint, so layout is final by the time we dispatch.
    const id = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => window.cancelAnimationFrame(id);
  }, [gameInMain, game]);

  return (
    <>
      <PractitionerCameraHost portalNode={cameraPortalNode} isActive={mainHasCamera} />
      <PractitionerMediaPreviewHost portalNode={mediaPreviewPortalNode} isActive={mainHasMedia} />
      {/*
        Child landscape on its own layer at z-index 0 — BELOW the logo (z-index 1) —
        so the logo stays visible over the landscape. The game canvas below sits at
        the slot's z-index 4 (ABOVE the logo) and is transparent except for the task
        sprites, so the logo shows through while the ball/penguin render on top of it.
        Same geometry/class as the canvas slot, so the two layers stay aligned.
      */}
      {showChildBackground ? (
        <Box
          sx={PractitionerPageStyles.mainGameSlot}
          className="game-promoted child-theme"
          style={{ zIndex: 0 }}
          aria-hidden
        />
      ) : null}
      <Box sx={PractitionerPageStyles.mainGameSlot} className={gameSlotClass} aria-hidden={!gameInMain}>
        <PractitionerGameHost portalNode={gamePortalNode} isActive={gameInMain} isOverlay />
      </Box>
      {isCallActive ? (
        // z-index 2 keeps the self-view BELOW the controls bar (z-index 3) so it never
        // covers the call/mute buttons. The promoted game's bottom sits above the
        // self-view (its bottom floor clears the self-view's top), so the game doesn't
        // cover the camera either.
        <div style={{ position: 'absolute', zIndex: 2, width: '250px', bottom: '80px', left: 0 }}>
          <LocalVideoPreview />
        </div>
      ) : null}
    </>
  );
};

interface PortalSourcesProps {
  cameraPortalNode: HTMLDivElement | null;
  mediaPreviewPortalNode: HTMLDivElement | null;
  isCallActive: boolean;
}

const PortalSources = ({ cameraPortalNode, mediaPreviewPortalNode, isCallActive }: PortalSourcesProps) => {
  const { viewSwapped, isMediaLoaded, hasBeenSaved } = usePractitionerControlsContext();
  const hasMedia = Boolean(isMediaLoaded && hasBeenSaved);
  // media is in footer when (call AND not swapped) OR (no call AND swapped) — matches ControlPanelFooter
  const mediaInFooter = hasMedia && isCallActive !== viewSwapped;
  const mediaPreviewClassName = mediaInFooter ? 'client-preview' : 'practitioner-preview';

  return (
    <>
      <PractitionerMediaPreviewPortal portalNode={mediaPreviewPortalNode} className={mediaPreviewClassName} />
      <PractitionerCameraPortal portalNode={cameraPortalNode} isEnabled={isCallActive} />
    </>
  );
};

// In pre-call, isCallActive is effectively false, so gameInMain collapses to viewSwapped && hasMedia.
const usePreCallGameInMain = () => {
  const { viewSwapped, isMediaLoaded, hasBeenSaved } = usePractitionerControlsContext();
  const hasMedia = Boolean(isMediaLoaded && hasBeenSaved);
  return viewSwapped && hasMedia;
};

const PreCallStatusMediaImage = ({ isClientPageReady }: { isClientPageReady: boolean }) => {
  const { isPractitionerPlayerVisible } = usePractitionerControlsContext();
  const gameInMain = usePreCallGameInMain();
  if (isPractitionerPlayerVisible || gameInMain) {
    return null;
  }
  return (
    <Box sx={PractitionerPageStyles.preCallStatusMedia} aria-hidden="true">
      <img
        src={
          isClientPageReady
            ? 'assets/clientConnected/client-ready-cloud.gif'
            : 'assets/waitingForClient/loading-client-not-connected-cloud.gif'
        }
        alt=""
      />
    </Box>
  );
};

interface PreCallTextProps {
  isClientPageReady: boolean;
  isLinkCopied: boolean;
  onCopySessionLink: () => void;
}

const PreCallTextOverlay = ({ isClientPageReady, isLinkCopied, onCopySessionLink }: PreCallTextProps) => {
  const { t } = useTranslation();
  const { sessionId, treatmentId } = useAppContext();
  const sessionCode = sessionId && treatmentId ? getSessionCode(sessionId, treatmentId) : '';
  const gameInMain = usePreCallGameInMain();
  if (gameInMain) {
    return null;
  }
  return (
    <>
      <Box sx={PractitionerPageStyles.callButtonTextStack}>
        <Box component="p" sx={PractitionerPageStyles.headline}>
          {isClientPageReady ? t('client-joined-session') : t('waiting-for-client-to-join')}
          {sessionCode ? (
            <Box component="span" sx={PractitionerPageStyles.sessionCodeInline}>
              {sessionCode}
            </Box>
          ) : null}
        </Box>
        <Box component="p" sx={PractitionerPageStyles.subline}>
          {isClientPageReady ? t('ready-to-start-call') : t('share-session-link-hint')}
        </Box>
      </Box>
      {!isClientPageReady ? (
        <Box sx={PractitionerPageStyles.copyLinkGroup}>
          <Tooltip title={t('click-to-copy-session') as string} placement="bottom">
            <Box
              component="button"
              type="button"
              sx={PractitionerPageStyles.copyLinkCircleButton}
              onClick={onCopySessionLink}
            >
              <img src="assets/icons/content_copy_24dp.svg" alt={t('click-to-copy-session') as string} />
            </Box>
          </Tooltip>
          {isLinkCopied ? (
            <Box component="span" sx={PractitionerPageStyles.copiedTag}>
              {t('link-copied')}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </>
  );
};

interface CallButtonOverlaySlotProps {
  hostRef: (el: HTMLDivElement | null) => void;
  dataState?: string;
  isCallActive: boolean;
  isPreCall: boolean;
  isClientPageReady: boolean;
  isLinkCopied: boolean;
  onCopySessionLink: () => void;
}

// Hosts the (portaled) start-call button. Lives under the controls provider so it
// can read the media state and lower the button when pre-call content fills the main
// area. Only adds a class — the ref/portal wiring is unchanged.
const CallButtonOverlaySlot = ({
  hostRef,
  dataState,
  isCallActive,
  isPreCall,
  isClientPageReady,
  isLinkCopied,
  onCopySessionLink,
}: CallButtonOverlaySlotProps) => {
  const { isMediaLoaded, hasBeenSaved } = usePractitionerControlsContext();
  const hasMedia = Boolean(isMediaLoaded && hasBeenSaved);
  // Lower the button to the game's spot whenever pre-call content fills the main area
  // — the game (when swapped) OR the media preview (when not). The accompanying text
  // is kept/hidden by PreCallTextOverlay (hidden only when the game is in main).
  const buttonLowered = !isCallActive && hasMedia;
  return (
    <Box
      ref={hostRef}
      sx={PractitionerPageStyles.callButtonOverlay}
      className={buttonLowered ? 'game-promoted' : undefined}
      data-state={dataState}
    >
      {isPreCall ? (
        <PreCallTextOverlay
          isClientPageReady={isClientPageReady}
          isLinkCopied={isLinkCopied}
          onCopySessionLink={onCopySessionLink}
        />
      ) : null}
    </Box>
  );
};

function setWindowSize() {
  const width = 100 + 'vw';
  const height = 100 + 'vh';
  return [width, height];
}

const AlertTransition = (props: SlideProps) => <Slide {...props} direction="left" />;

const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(errorMessage));
    }, timeoutMs);

    promise
      .then((value) => {
        clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });

export default function PractitionerPage(props: any) {
  useAuthorizedPractitioner();
  const { sessionId, treatmentId, hasDevice } = useAppContext();
  const roomState = useRoomState();
  const [practitionerIsConnected, setPractitionerIsConnected] = useState<boolean>(false);
  const [width, height] = setWindowSize();
  const { isEnabled, setVideoViewState } = useVideoViewToggle(false);
  const { room, connect, localTracks, getLocalAudioTrack, getLocalVideoTrack } = useVideoContext();
  const [collapsed, setCollapsed] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [clientIsConnected, setClientIsConnected] = useState(false);
  const [isClientPageReady, setIsClientPageReady] = useState(false);
  // EMDR-Plus (hasDevice) sessions use the VR headset, not the web ClientPage, so
  // web-client presence never arrives — bypass the gate so the call can be started.
  const isReadyForCall = hasDevice || isClientPageReady;
  const [callAbortSignal, setCallAbortSignal] = useState(0);
  const [userStoppedCall, setUserStoppedCall] = useState(false);
  const [requiresManualRecall, setRequiresManualRecall] = useState(false);
  const [callStartFailed, setCallStartFailed] = useState(false);
  const [offlineNotificationOpen, setOfflineNotificationOpen] = useState(false);
  const [participantIsReconnecting, setParticipantIsReconnecting] = useState(false);
  const networkReconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const networkReconnectBannerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const networkReconnectWasActiveRef = useRef(false);
  const [showNetworkReconnectingBanner, setShowNetworkReconnectingBanner] = useState(false);
  const { t } = useTranslation();
  const currentState = useCurrentSessionState() || {};
  const [waitingForClient, setWaitingForClient] = useState(false);
  const controlPanelStopCallHandlerRef = useRef<(() => void) | null>(null);
  const initialPermissionReminderCheckedRef = useRef(false);

  const mediaPreviewPortalNodeRef = useRef<HTMLDivElement | null>(null);
  const cameraPortalNodeRef = useRef<HTMLDivElement | null>(null);
  const gamePortalNodeRef = useRef<HTMLDivElement | null>(null);

  if (typeof document !== 'undefined') {
    if (!mediaPreviewPortalNodeRef.current) {
      mediaPreviewPortalNodeRef.current = document.createElement('div');
    }
    if (!cameraPortalNodeRef.current) {
      cameraPortalNodeRef.current = document.createElement('div');
      cameraPortalNodeRef.current.style.width = '100%';
      cameraPortalNodeRef.current.style.height = '100%';
    }
    if (!gamePortalNodeRef.current) {
      // Phaser config targets parent by id; keep #client-task-container regardless of where the node lives.
      gamePortalNodeRef.current = document.createElement('div');
      gamePortalNodeRef.current.id = 'client-task-container';
      gamePortalNodeRef.current.style.width = '100%';
      gamePortalNodeRef.current.style.height = '100%';
    }
  }

  const mediaPreviewPortalNode = mediaPreviewPortalNodeRef.current;
  const cameraPortalNode = cameraPortalNodeRef.current;
  const gamePortalNode = gamePortalNodeRef.current;
  const [callButtonHostEl, setCallButtonHostEl] = useState<HTMLDivElement | null>(null);
  const [inCallButtonHostEl, setInCallButtonHostEl] = useState<HTMLDivElement | null>(null);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const linkCopiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopySessionLink = useCallback(() => {
    const url = `https://emdr-remote.psylaris.nl/clientpage?treatmentUUID=${treatmentId}&sessionUUID=${sessionId}&deviceId=`;
    try {
      void navigator.clipboard?.writeText(url);
      setIsLinkCopied(true);
      if (linkCopiedTimeoutRef.current !== null) {
        clearTimeout(linkCopiedTimeoutRef.current);
      }
      linkCopiedTimeoutRef.current = setTimeout(() => {
        setIsLinkCopied(false);
        linkCopiedTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      console.error('[PractitionerPage] Failed to copy link', err);
    }
  }, [sessionId, treatmentId]);

  useEffect(() => {
    return () => {
      if (linkCopiedTimeoutRef.current !== null) {
        clearTimeout(linkCopiedTimeoutRef.current);
        linkCopiedTimeoutRef.current = null;
      }
    };
  }, []);

  const handleDisconnectUser = useCallback(() => {
    if (room && typeof room.disconnect === 'function') {
      room.disconnect();
      setPractitionerIsConnected(false);
      setVideoViewState(false);
    }
  }, [room, setVideoViewState]);

  const StopCall = useCallback(() => {
    if (room && typeof room.disconnect === 'function') {
      room.disconnect();
    }
    setVideoViewState(false);
  }, [room, setVideoViewState]);

  const [showPermissionReminder, setShowPermissionReminder] = useState(false);
  const [token, setToken] = useState<string>('');

  const ensureCallLocalMediaTracks = useCallback(
    () =>
      ensureLocalMediaTracks({
        localTracks,
        getLocalAudioTrack,
        getLocalVideoTrack,
      }),
    [getLocalAudioTrack, getLocalVideoTrack, localTracks]
  );

  useEffect(() => {
    const roomAny = room as any;
    const handleParticipantDisconnected = () => setParticipantIsReconnecting(false);
    const handleParticipantReconnecting = () => setParticipantIsReconnecting(true);
    const handleParticipantReconnected = () => setParticipantIsReconnecting(false);

    // Recommended cleanup on browser unload
    window.addEventListener('unload', handleDisconnectUser);

    // Only add listeners if room exists
    if (roomAny && typeof roomAny.on === 'function') {
      roomAny.on('participantDisconnected', handleParticipantDisconnected);
      roomAny.on('participantReconnecting', handleParticipantReconnecting);
      roomAny.on('participantReconnected', handleParticipantReconnected);
    }

    return () => {
      window.removeEventListener('unload', handleDisconnectUser);

      if (roomAny && typeof roomAny.off === 'function') {
        roomAny.off('participantDisconnected', handleParticipantDisconnected);
        roomAny.off('participantReconnecting', handleParticipantReconnecting);
        roomAny.off('participantReconnected', handleParticipantReconnected);
      }
    };
  }, [room, handleDisconnectUser]);

  useEffect(() => {
    if (initialPermissionReminderCheckedRef.current) {
      return;
    }

    initialPermissionReminderCheckedRef.current = true;
    let isMounted = true;

    const checkInitialPermissions = async () => {
      const permissionStatus = await getMediaPermissionStatus();

      if (isMounted && permissionStatus.shouldShowInitialReminder) {
        setShowPermissionReminder(true);
      }
    };

    void checkInitialPermissions();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleOnline = () => {
      setOfflineNotificationOpen(false);
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    const resetPLayerState = async () => {
      await BackendService.sendMediaVisibility(sessionId, treatmentId, '', false);
      await BackendService.sendMediaCommand(sessionId, treatmentId, '', 1, 0);
    };
    resetPLayerState();
  }, [sessionId, treatmentId]);

  const isNetworkReconnecting =
    (roomState === 'reconnecting' || participantIsReconnecting) &&
    practitionerIsConnected &&
    !waitingForClient &&
    !requiresManualRecall &&
    !callStartFailed &&
    !userStoppedCall;

  useEffect(() => {
    if (networkReconnectWasActiveRef.current !== isNetworkReconnecting) {
      networkReconnectWasActiveRef.current = isNetworkReconnecting;
      metrics.count('call.state.transition', 1, {
        state: isNetworkReconnecting ? 'network_reconnecting' : 'network_reconnected',
      });
    }

    if (networkReconnectTimeoutRef.current !== null) {
      clearTimeout(networkReconnectTimeoutRef.current);
      networkReconnectTimeoutRef.current = null;
    }

    if (!isNetworkReconnecting) {
      return;
    }

    networkReconnectTimeoutRef.current = setTimeout(() => {
      const stillReconnecting =
        (roomState === 'reconnecting' || participantIsReconnecting) &&
        practitionerIsConnected &&
        !waitingForClient &&
        !requiresManualRecall &&
        !callStartFailed &&
        !userStoppedCall;

      if (!stillReconnecting) {
        return;
      }

      setWaitingForClient(false);
      setRequiresManualRecall(true);
      setIsConnecting(false);
      setPractitionerIsConnected(false);
      setClientIsConnected(false);
      StopCall();
      metrics.count('call.state.transition', 1, {
        state: 'recall_required',
        reason: 'network_reconnect_timeout',
      });
    }, NETWORK_RECONNECT_TIMEOUT_MS);

    return () => {
      if (networkReconnectTimeoutRef.current !== null) {
        clearTimeout(networkReconnectTimeoutRef.current);
        networkReconnectTimeoutRef.current = null;
      }
    };
  }, [
    isNetworkReconnecting,
    roomState,
    participantIsReconnecting,
    practitionerIsConnected,
    waitingForClient,
    requiresManualRecall,
    callStartFailed,
    userStoppedCall,
    StopCall,
  ]);

  useEffect(() => {
    if (networkReconnectBannerTimeoutRef.current !== null) {
      clearTimeout(networkReconnectBannerTimeoutRef.current);
      networkReconnectBannerTimeoutRef.current = null;
    }

    if (isNetworkReconnecting) {
      setShowNetworkReconnectingBanner(true);
      return;
    }

    if (!showNetworkReconnectingBanner) {
      return;
    }

    networkReconnectBannerTimeoutRef.current = setTimeout(() => {
      setShowNetworkReconnectingBanner(false);
      networkReconnectBannerTimeoutRef.current = null;
    }, NETWORK_RECONNECT_BANNER_LINGER_MS);

    return () => {
      if (networkReconnectBannerTimeoutRef.current !== null) {
        clearTimeout(networkReconnectBannerTimeoutRef.current);
        networkReconnectBannerTimeoutRef.current = null;
      }
    };
  }, [isNetworkReconnecting, showNetworkReconnectingBanner]);

  useEffect(() => {
    if (requiresManualRecall || callStartFailed || userStoppedCall || !practitionerIsConnected) {
      if (networkReconnectBannerTimeoutRef.current !== null) {
        clearTimeout(networkReconnectBannerTimeoutRef.current);
        networkReconnectBannerTimeoutRef.current = null;
      }
      setShowNetworkReconnectingBanner(false);
    }
  }, [requiresManualRecall, callStartFailed, userStoppedCall, practitionerIsConnected]);

  const ConnectToRoom = async (token: string, dataTrack: LocalDataTrack) => {
    // check if user provided access to the video and microphone devices
    const success = await requestMedia({ setPermission: setShowPermissionReminder });

    if (!success) {
      setPractitionerIsConnected(false);
      setClientIsConnected(false);
      setIsConnecting(false);
      StopCall();
      return;
    }

    setShowPermissionReminder(false);
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setIsConnecting(false);
      setPractitionerIsConnected(false);
      setClientIsConnected(false);
      setVideoViewState(false);
      setWaitingForClient(false);
      setCallStartFailed(false);
      setRequiresManualRecall(false);
      setOfflineNotificationOpen(true);
      metrics.count('call.state.transition', 1, {
        state: 'call_start_blocked',
        stage: 'offline_connect_precheck',
      });
      return;
    }

    try {
      await ensureCallLocalMediaTracks();
      await withTimeout(connect(token, dataTrack), CALL_CONNECT_TIMEOUT_MS, 'Twilio connect timed out');
      setIsConnecting(false);
      setPractitionerIsConnected(true);
      setVideoViewState(true);
      setWaitingForClient(false);
      setRequiresManualRecall(false);
      setCallStartFailed(false);
      setUserStoppedCall(false);
      metrics.count('call.state.transition', 1, { state: 'connected' });
    } catch (err) {
      console.error('[Twilio Connect Error]', err);
      setIsConnecting(false);
      setPractitionerIsConnected(false);
      setVideoViewState(false);
      setWaitingForClient(false);
      setCallStartFailed(true);
      setRequiresManualRecall(false);
      metrics.count('call.state.transition', 1, {
        state: 'call_start_failed',
        stage: err?.message === 'Twilio connect timed out' ? 'connect_timeout' : 'connect_to_room',
      });
    }
  };

  const handleUserStoppedCall = useCallback(() => {
    setUserStoppedCall(true);
    setCallAbortSignal((value) => value + 1);
    setWaitingForClient(false);
    setRequiresManualRecall(false);
    setCallStartFailed(false);
    setParticipantIsReconnecting(false);
    metrics.count('call.state.transition', 1, { state: 'manual_stop' });
  }, []);

  const handleRecallRequired = useCallback(() => {
    setWaitingForClient(false);
    setRequiresManualRecall(true);
    setCallStartFailed(false);
    metrics.count('call.state.transition', 1, {
      state: 'recall_required',
      reason: 'client_wait_timeout',
    });
  }, []);

  const abortCallAndRetries = useCallback(() => {
    handleUserStoppedCall();
    setPractitionerIsConnected(false);
    setClientIsConnected(false);
    setIsConnecting(false);
    StopCall();
  }, [handleUserStoppedCall, StopCall]);

  const stopCallFromBanner = useCallback(() => {
    const stopHandler = controlPanelStopCallHandlerRef.current;
    if (typeof stopHandler === 'function') {
      stopHandler();
      return;
    }

    abortCallAndRetries();
  }, [abortCallAndRetries]);

  const registerControlPanelStopCallHandler = useCallback((handler: () => void) => {
    controlPanelStopCallHandlerRef.current = handler;
  }, []);

  const toggleCallHasStarted = async () => {
    setUserStoppedCall(false);
    setRequiresManualRecall(false);
    setCallStartFailed(false);
    setOfflineNotificationOpen(false);

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setIsConnecting(false);
      setCallStartFailed(false);
      setOfflineNotificationOpen(true);
      metrics.count('call.state.transition', 1, {
        state: 'call_start_blocked',
        stage: 'offline_start_precheck',
      });
      return;
    }

    setIsConnecting(true);
    metrics.count('call.action.start_attempt', 1);
    const mediaAccessGranted = await requestMedia({ setPermission: setShowPermissionReminder });

    if (!mediaAccessGranted) {
      setPractitionerIsConnected(false);
      setClientIsConnected(false);
      setVideoViewState(false);
      setIsConnecting(false);
      return;
    }

    setShowPermissionReminder(false);

    try {
      await ensureCallLocalMediaTracks();
      const token = await withTimeout(
        BackendService.getToken(sessionId, treatmentId),
        CALL_START_TOKEN_TIMEOUT_MS,
        'Token request timed out'
      );
      setToken(token);
    } catch (error) {
      console.error('[Call Start Error]', error);
      setPractitionerIsConnected(false);
      setClientIsConnected(false);
      setVideoViewState(false);
      setCallStartFailed(true);
      setToken('');
      metrics.count('call.state.transition', 1, {
        state: 'call_start_failed',
        stage: error instanceof Error && error.message === 'Token request timed out' ? 'token_timeout' : 'token_fetch',
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleRetryCall = useCallback(() => {
    setUserStoppedCall(false);
    setRequiresManualRecall(false);
    setCallStartFailed(false);
    metrics.count('call.action.retry_manual', 1);
    void toggleCallHasStarted();
  }, [toggleCallHasStarted]);

  const showNetworkReconnectingAlert =
    showNetworkReconnectingBanner &&
    practitionerIsConnected &&
    !requiresManualRecall &&
    !callStartFailed &&
    !userStoppedCall;
  const showWaitingForClientAlert =
    waitingForClient && practitionerIsConnected && !clientIsConnected && !requiresManualRecall && !callStartFailed;

  const isPreCall = roomState === 'disconnected' && !isConnecting;
  const isClientJoinedAwaitingStart = isPreCall && isReadyForCall;
  const isWaitingForClient = isPreCall && !isReadyForCall;
  const callButtonDataState = isClientJoinedAwaitingStart
    ? 'awaiting-start'
    : isWaitingForClient
      ? 'waiting-for-client'
      : undefined;

  const stopCallBannerAction = (
    <Button color="inherit" size="small" onClick={stopCallFromBanner}>
      {t('stop-call') as string}
    </Button>
  );

  const activeBanner = showNetworkReconnectingAlert
    ? {
        severity: 'info' as const,
        message: 'connection-interrupted',
        testId: 'pwid-practitioner-network-reconnecting',
        action: stopCallBannerAction,
        zIndex: 1301,
      }
    : showWaitingForClientAlert
      ? {
          severity: 'warning' as const,
          message: 'reconnection-waiting-client',
          testId: 'pwid-practitioner-waiting',
          action: stopCallBannerAction,
          zIndex: 1300,
        }
      : offlineNotificationOpen
        ? {
            severity: 'error' as const,
            message: 'connection-call-failed',
            testId: 'pwid-practitioner-offline-notification',
            action: undefined,
            zIndex: 1300,
          }
        : null;
  const activePermissionReminder = showPermissionReminder
    ? {
        imageSrc: permissionImage,
        buttonLabel: 'okay-internet-report',
        message: 'permission-allow-camera-mic',
        testId: 'pwid-practitioner-permission',
        onClose: () => setShowPermissionReminder(false),
      }
    : requiresManualRecall
      ? {
          imageSrc: waitingForClientIMG,
          buttonLabel: 'retry',
          closeLabel: 'stop-call',
          message: 'reconnection-call-again',
          testId: 'pwid-practitioner-recall-required',
          onClose: abortCallAndRetries,
          onRetry: handleRetryCall,
        }
      : callStartFailed
        ? {
            imageSrc: waitingForClientIMG,
            buttonLabel: 'retry',
            closeLabel: 'stop-call',
            message: 'reconnection-failed-call-again',
            testId: 'pwid-practitioner-call-start-failed',
            onClose: abortCallAndRetries,
            onRetry: handleRetryCall,
          }
        : null;

  return (
    <Container style={{ height, width }}>
      <PractitionerControlsProvider>
        <MediaProvider>
          <UploadProvider>
            <PortalSources
              cameraPortalNode={cameraPortalNode}
              mediaPreviewPortalNode={mediaPreviewPortalNode}
              isCallActive={isEnabled}
            />
            <VideoLoader isLoading={isConnecting} />
            {collapsed ? null : (
              <Main className={'main'}>
                <Box sx={practitionerWrapper}>
                  <img
                    src={hasDevice ? 'assets/logo-no-bg-light-plus.png' : 'assets/logo-no-bg-light.png'}
                    alt="psylaris | emdr-remote"
                    style={{ height: '70px' }}
                  />
                  <AcceptanceBadge />
                </Box>
                <MainAreaSlots
                  cameraPortalNode={cameraPortalNode}
                  mediaPreviewPortalNode={mediaPreviewPortalNode}
                  gamePortalNode={gamePortalNode}
                  isCallActive={isEnabled}
                />
              </Main>
            )}
            {isPreCall && (
              <Box
                sx={PractitionerPageStyles.preCallBanner}
                data-testid={
                  isClientJoinedAwaitingStart ? 'pwid-practitioner-state2-banner' : 'pwid-practitioner-state1-banner'
                }
              >
                <PreCallStatusMediaImage isClientPageReady={isReadyForCall} />
              </Box>
            )}
            <CallButtonOverlaySlot
              hostRef={setCallButtonHostEl}
              dataState={callButtonDataState}
              isCallActive={isEnabled}
              isPreCall={isPreCall}
              isClientPageReady={isReadyForCall}
              isLinkCopied={isLinkCopied}
              onCopySessionLink={handleCopySessionLink}
            />
            <ControlsPractitioner currentState={currentState} setInCallButtonHostEl={setInCallButtonHostEl} />
            <div className={`${collapsed ? 'right-full-width' : 'right'}`}>
              {/*<CollapseToggle isCollapsed={collapsed} handleCollapse={() => setCollapsed(!collapsed)} />*/}
              <ControlPanel
                ConnectToRoom={ConnectToRoom}
                StopCall={StopCall}
                practitionerIsConnected={practitionerIsConnected}
                isNetworkReconnecting={isNetworkReconnecting}
                setPractitionerIsConnected={setPractitionerIsConnected}
                currentState={currentState}
                isCallActive={isEnabled}
                clientIsConnected={clientIsConnected}
                setClientIsConnected={setClientIsConnected}
                setWaitingForClient={setWaitingForClient}
                toggleCallHasStarted={toggleCallHasStarted}
                token={token}
                setToken={setToken}
                callAbortSignal={callAbortSignal}
                userStoppedCall={userStoppedCall}
                onRecallRequired={handleRecallRequired}
                onUserStoppedCall={handleUserStoppedCall}
                registerStopCallHandler={registerControlPanelStopCallHandler}
                mediaPreviewPortalNode={mediaPreviewPortalNode}
                cameraPortalNode={cameraPortalNode}
                gamePortalNode={gamePortalNode}
                callButtonHostEl={callButtonHostEl}
                inCallButtonHostEl={inCallButtonHostEl}
                onClientPageReadyChange={setIsClientPageReady}
                canStartCall={isReadyForCall}
              />
            </div>
            <SessionCheckDialog sessionId={sessionId} treatmentId={treatmentId} />
            {activeBanner ? (
              <Snackbar
                open
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                sx={{ zIndex: activeBanner.zIndex }}
                onClose={
                  activeBanner.testId === 'pwid-practitioner-offline-notification'
                    ? () => setOfflineNotificationOpen(false)
                    : undefined
                }
                TransitionComponent={AlertTransition}
                transitionDuration={ALERT_TRANSITION_MS}
              >
                <Alert
                  severity={activeBanner.severity}
                  variant="filled"
                  data-testid={activeBanner.testId}
                  action={activeBanner.action}
                >
                  {t(activeBanner.message)}
                </Alert>
              </Snackbar>
            ) : null}
            <PermissionReminder
              open={Boolean(activePermissionReminder)}
              onClose={activePermissionReminder?.onClose ?? (() => undefined)}
              onRetry={activePermissionReminder?.onRetry}
              imageSrc={activePermissionReminder?.imageSrc}
              buttonLabel={activePermissionReminder?.buttonLabel}
              closeLabel={activePermissionReminder?.closeLabel}
              message={activePermissionReminder?.message}
              pwid={activePermissionReminder?.testId}
            />
          </UploadProvider>
        </MediaProvider>
      </PractitionerControlsProvider>
    </Container>
  );
}
